import React, { useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { FaEllipsisH, FaHeartbeat, FaShieldAlt, FaSyncAlt, FaTrash } from 'react-icons/fa'
import { Button, DropdownMenu, Switch } from '@components'
import { Spinner } from '@components/ui/Primitives'
import { useOnClickOutside } from '@hooks'
import type { IMcpConnection } from '@query'
import { ProviderAvatar, StatusPill } from './shared'
import { ToolApprovalPanel } from './ToolApprovalPanel'
import type { ApprovalPatch } from './toolPermissions'

interface Props {
  connection: IMcpConnection
  toggling: boolean
  deleting: boolean
  testing: boolean
  loadingTools: boolean
  savingApproval: boolean
  onToggle: (enabled: boolean) => void
  onReconnect: () => void
  onDisconnect: () => void
  onTest: () => void
  onSaveApproval: (patch: ApprovalPatch) => void
  onLoadTools: () => void
}

export const ConnectionCard: React.FC<Props> = ({
  connection,
  toggling,
  deleting,
  testing,
  loadingTools,
  savingApproval,
  onToggle,
  onReconnect,
  onDisconnect,
  onTest,
  onSaveApproval,
  onLoadTools
}) => {
  const [t] = useTranslation()
  const [approvalOpen, setApprovalOpen] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  // Click anywhere outside the card closes the open tool-permissions section.
  useOnClickOutside(cardRef, () => setApprovalOpen(false))
  const canReconnect = connection.auth_type === 'oauth' || connection.status === 'needs_reauth'
  const busy = deleting || testing
  const approvalCount = connection.approval_tools?.length ?? 0
  const toolCount = connection.available_tools?.length ?? 0
  const toolsSummary =
    toolCount > 0
      ? t('lucaSettings.toolsSummary', { tools: toolCount, approvals: approvalCount })
      : t('lucaSettings.toolsNone')

  const menuItems = [
    <Button key="test" icon={<FaHeartbeat />} onClick={onTest}>
      {t('lucaSettings.test')}
    </Button>,
    ...(canReconnect
      ? [
          <Button key="reconnect" icon={<FaSyncAlt />} onClick={onReconnect}>
            {t('lucaSettings.reconnect')}
          </Button>
        ]
      : []),
    <Button key="disconnect" intent="danger" icon={<FaTrash />} onClick={onDisconnect}>
      {t('lucaSettings.disconnect')}
    </Button>
  ]

  return (
    <Card ref={cardRef}>
      <Row>
        <Main>
          <ProviderAvatar templateKey={connection.template_key} size={2.75} />
          <Info>
            <TopRow>
              <Name>{connection.display_name}</Name>
              <AuthBadge>{t(`lucaSettings.authBadge.${connection.auth_type}`)}</AuthBadge>
            </TopRow>
            <Url>{connection.server_url}</Url>
            <Meta>
              <StatusPill status={connection.status} />
              <MetaSep>·</MetaSep>
              <ToolsSummary>{toolsSummary}</ToolsSummary>
            </Meta>
          </Info>
        </Main>

        <Right>
          <ToggleWrap>
            <Switch
              id={`luca-connection-enabled-${connection.id}`}
              checked={connection.enabled}
              disabled={connection.status === 'pending' || toggling}
              label={t('lucaSettings.enabled')}
              onChange={e => onToggle(e.target.checked)}
            />
          </ToggleWrap>
          <ShieldWrap>
            <ShieldButton
              $active={approvalOpen}
              isSecondary
              size="sm"
              icon={<FaShieldAlt />}
              title={t('lucaSettings.approval.manage')}
              onClick={() => setApprovalOpen(open => !open)}
            />
            {approvalCount > 0 && <CountBadge>{approvalCount}</CountBadge>}
          </ShieldWrap>
          {busy ? (
            <Spinner size={16} margin={4} />
          ) : (
            <DropdownMenu items={menuItems}>
              <ControlButton
                isSecondary
                size="sm"
                icon={<FaEllipsisH />}
                title={t('lucaSettings.actions')}
              />
            </DropdownMenu>
          )}
        </Right>
      </Row>

      {approvalOpen && (
        <ToolApprovalPanel
          connection={connection}
          saving={savingApproval}
          loadingTools={loadingTools}
          onSave={onSaveApproval}
          onLoadTools={onLoadTools}
        />
      )}
    </Card>
  )
}

const Card = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem;
`

// Avatar + info group. On a narrow container (e.g. the settings column while the Luca chat is
// open) this takes the full width so the controls wrap to their own line instead of overlapping.
const Main = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  flex: 1 1 auto;
  min-width: 0;

  @container (max-width: 38rem) {
    flex-basis: 100%;
  }
`

const Info = styled.div`
  flex: 1;
  min-width: 0;
`

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const Name = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.luca.navy};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const AuthBadge = styled.span`
  flex: 0 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.xs}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.iconGrayDark};
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`

const Url = styled.div`
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-family: ui-monospace, Menlo, monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
`

const MetaSep = styled.span`
  color: ${({ theme }) => `color-mix(in srgb, ${theme.colors.iconGray} 45%, white)`};
`

const ToolsSummary = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  flex: 0 0 auto;

  /* When the card wraps, the controls take their own line, aligned to the right. */
  @container (max-width: 38rem) {
    width: 100%;
    justify-content: flex-end;
  }
`

// Switch renders its own <label> after the toggle, so the row is reversed to keep the label on
// the left of the pill, and the shared wrapper's bottom margin is dropped so the label and the
// pill stay optically centered with the shield/kebab buttons.
const ToggleWrap = styled.div`
  display: inline-flex;
  align-items: center;

  .switch-wrapper {
    flex-direction: row-reverse;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xs}rem;
    margin-bottom: 0;
  }

  .switch-wrapper > label {
    font-size: ${({ theme }) => theme.fontSize.xs}rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.iconGray};
    white-space: nowrap;

    /* Container-based: hide the label only when the card itself is very narrow. */
    @container (max-width: 20rem) {
      display: none;
    }
  }
`

// Lets the shield button carry an absolutely-positioned approval count without changing its
// box, so it stays the same size/height as the sibling kebab button.
const ShieldWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`

const CountBadge = styled.span`
  position: absolute;
  top: -0.3rem;
  right: -0.3rem;
  min-width: 0.95rem;
  height: 0.95rem;
  padding: 0 0.2rem;
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.luca.deepBlue};
  color: ${({ theme }) => theme.colors.neutralWhite};
  font-size: 0.6rem;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`

// Shield + kebab share one fixed 34x34 square box so they line up exactly (and match the
// design's icon buttons) regardless of the base Button's default sizing.
const ControlButton = styled(Button)`
  && {
    width: 2.15rem;
    min-width: 2.15rem;
    height: 2.15rem;
    padding: 0;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius.md}rem;

    svg {
      width: 1.05rem;
      height: 1.05rem;
    }
  }
`

const ShieldButton = styled(ControlButton)<{ $active?: boolean }>`
  && {
    ${({ theme, $active }) =>
      $active &&
      css`
        border-color: ${theme.luca.tintBorder};
        background: ${theme.luca.tint};
        color: ${theme.luca.deepBlue};
      `}
  }
`
